import {
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  IconButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  faChevronRight,
  faArrowLeft,
  faPlayCircle,
  faFolderPlus,
  faPlusCircle,
} from '@fortawesome/free-solid-svg-icons';
import React, {
  useState,
  Fragment,
  useMemo,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  AlgorithmSettingsProps,
  algorithmSettingsRegistry,
  AutoShapeAlgorithmOptionsMap,
  getDefaultAlgorithmOptions,
} from '../auto-shapes/types';
import { ViewerMode } from '../common/viewer-mode';
import { InsufficientCreditsModal } from '../common/InsufficientCreditsModal';
import { AutoShapeAlgorithmType, Point3 } from 'src/gql/graphql';
import { getAlgorithmMetadata } from 'src/hooks/auto-shapes';
import { useCredits, useTenantCredits } from 'src/hooks/credits';
import { useUserTenant } from 'src/services/auth-info';

type AlgorithmState<T extends AutoShapeAlgorithmType> = {
  type: T;
  options: AutoShapeAlgorithmOptionsMap[T];
};

interface AutoshapePopoverProps {
  viewerMode: ViewerMode;
  createAutoShape: <T extends AutoShapeAlgorithmType>(
    name: string,
    algorithmType: T,
    algorithmInput: AutoShapeAlgorithmOptionsMap[T]
  ) => void;
  onClose: () => void;
  findNextAvailableAutoShapeName: (name: string) => string;
  modelUrn: string; // kept for API parity with caller, not directly used here
  modelId: string;
  globalOffset: Point3;
  sheetId: string;
  pageNumber: number;
  templateRect: { x: number; y: number; width: number; height: number };
  sensitivity: number;
  /** Whether autoshapes feature is enabled */
  enableAutoShapes?: boolean;
  /** Callback to create a new group/folder */
  onCreateGroup?: () => void;
  /** The path of the currently active folder (for placing new autoshapes) */
  activeFolderPath?: string | null;
  /** Project ID for credit checking */
  projectId?: string;
  /** Callback when preventClose state changes (for controlling Menu closeOnBlur) */
  onPreventCloseChange?: (preventClose: boolean) => void;
}

// Context for settings components to request preventClose
export const PreventCloseContext = createContext<
  ((preventClose: boolean) => void) | null
>(null);

export const usePreventClose = () => {
  const setPreventClose = useContext(PreventCloseContext);
  return setPreventClose;
};

// eslint-disable-next-line complexity
export const AutoshapePopover = ({
  // modelUrn is intentionally not destructured to avoid unused variable linting
  viewerMode,
  createAutoShape,
  onClose,
  findNextAvailableAutoShapeName,
  modelId,
  globalOffset,
  sheetId,
  pageNumber,
  templateRect,
  sensitivity,
  enableAutoShapes = true,
  onCreateGroup,
  activeFolderPath,
  projectId,
  onPreventCloseChange,
}: AutoshapePopoverProps) => {
  const { t } = useTranslation('auto-shapes');
  const { t: tHeader } = useTranslation('header');
  const creditsInfo = useCredits(projectId);
  const { credits: tenantCredits } = useTenantCredits();
  const { isAdmin } = useUserTenant();
  const {
    isOpen: isCreditsModalOpen,
    onOpen: openCreditsModal,
    onClose: closeCreditsModal,
  } = useDisclosure();

  const [currentMenu, setCurrentMenu] = useState<'algorithms' | 'settings'>(
    'algorithms'
  );
  const [currentAlgorithmState, setCurrentAlgorithmState] =
    useState<AlgorithmState<AutoShapeAlgorithmType> | null>(null);

  const [isSettingsValid, setIsSettingsValid] = useState(false);
  const [preventClose, setPreventClose] = useState(false);

  // Notify parent when preventClose state changes
  useEffect(() => {
    onPreventCloseChange?.(preventClose);
  }, [preventClose, onPreventCloseChange]);

  const algorithms = useMemo(() => {
    return viewerMode === ViewerMode.Models
      ? Object.values(AutoShapeAlgorithmType).filter((algorithm) =>
          algorithm.includes('3D')
        )
      : Object.values(AutoShapeAlgorithmType).filter((algorithm) =>
          algorithm.includes('2D')
        );
  }, [viewerMode]);

  const renderSettings = useMemo(() => {
    if (!currentAlgorithmState?.type) return null;

    const type = currentAlgorithmState.type;
    const SettingsComponent = algorithmSettingsRegistry[
      type
    ] as React.ComponentType<AlgorithmSettingsProps<typeof type>>;

    return (
      <PreventCloseContext.Provider value={setPreventClose}>
        <SettingsComponent
          value={currentAlgorithmState.options}
          onChange={(options) =>
            setCurrentAlgorithmState({
              type: currentAlgorithmState.type,
              options,
            })
          }
          onValidityChange={setIsSettingsValid}
        />
      </PreventCloseContext.Provider>
    );
  }, [currentAlgorithmState?.type, currentAlgorithmState?.options]);

  const handleClose = useCallback(() => {
    setCurrentMenu('algorithms');
    setCurrentAlgorithmState(null);
    setPreventClose(false); // Reset preventClose when closing
    onClose();
  }, [onClose]);

  // Reset preventClose when algorithm changes
  useEffect(() => {
    if (!currentAlgorithmState?.type) {
      setPreventClose(false);
    }
  }, [currentAlgorithmState?.type]);

  // Allow external request to open Symbol Detection 2D settings directly
  useEffect(() => {
    const openSymbolDetection = () => {
      const algorithm = AutoShapeAlgorithmType.SymbolDetection_2D;
      setCurrentAlgorithmState({
        type: algorithm,
        options: getDefaultAlgorithmOptions(
          algorithm,
          modelId,
          globalOffset,
          [],
          sheetId,
          pageNumber,
          templateRect,
          sensitivity
        ),
      });
      setCurrentMenu('settings');
      // Once settings are visible, enter picking for the settings component
      window.setTimeout(() => {
        window.dispatchEvent(new Event('symbol-detection:start'));
      }, 0);
    };
    window.addEventListener(
      'autoshape:open:symbol-detection-2d',
      openSymbolDetection
    );
    return () =>
      window.removeEventListener(
        'autoshape:open:symbol-detection-2d',
        openSymbolDetection
      );
  }, [modelId, globalOffset, sheetId, pageNumber, templateRect, sensitivity]);

  // Hotkeys: Enter = Run (if settings open and valid), Space = Run and continue (re-enter picking)
  // We keep this local to the popover so it only runs when open
  // Note: we rely on the parent to open the popover via hotkeys in ShapesPanel
  // and on the Settings component to respond to picking events.
  // Minimal implementation: Enter triggers Run; Space triggers Run and emit start picking.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maybeRun = useCallback(
    (continuePicking: boolean) => {
      if (
        !currentAlgorithmState ||
        currentMenu !== 'settings' ||
        !isSettingsValid
      )
        return;
      const baseName = findNextAvailableAutoShapeName(
        getAlgorithmMetadata(currentAlgorithmState.type).name
      );
      // Prefix with active folder path if set
      const name = activeFolderPath
        ? `${activeFolderPath}/${baseName}`
        : baseName;
      createAutoShape(
        name,
        currentAlgorithmState.type,
        currentAlgorithmState.options as AutoShapeAlgorithmOptionsMap[typeof currentAlgorithmState.type]
      );

      if (continuePicking) {
        // Close and immediately re-open Symbol Detection and start picking
        const reopen = () => {
          try {
            const addButton = document.querySelector(
              'button[aria-label="add-shape-options"]'
            ) as HTMLButtonElement | null;
            if (addButton) addButton.click();
          } catch {
            // noop
          }
          try {
            window.dispatchEvent(
              new Event('autoshape:open:symbol-detection-2d')
            );
          } catch {
            console.error(
              '[AutoShapes] autoshape:open:symbol-detection-2d not dispatched'
            );
          }
        };
        // Schedule reopen after closing
        window.setTimeout(reopen, 0);
      }

      handleClose();
    },
    [
      activeFolderPath,
      currentAlgorithmState,
      currentMenu,
      isSettingsValid,
      createAutoShape,
      findNextAvailableAutoShapeName,
      handleClose,
    ]
  );

  // Determine if a key event originated from an editable element (inputs, textareas, selects, or contentEditable)
  const isEventFromEditable = (event: KeyboardEvent): boolean => {
    const target = event.target as HTMLElement | null;
    if (!target) return false;
    const isEditableElement = (el: HTMLElement | null): boolean => {
      let node: HTMLElement | null = el;
      while (node) {
        if (node.isContentEditable) return true;
        const tag = node.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')
          return true;
        const role = node.getAttribute('role');
        if (role === 'textbox' || role === 'combobox' || role === 'spinbutton')
          return true;
        node = node.parentElement;
      }
      return false;
    };
    return isEditableElement(target);
  };

  // Attach key handlers when the popover renders
  // We avoid useHotkeys here to keep the dependency minimal in this file
  // and to ensure the handler is active only while the popover is mounted
  // (MenuList is rendered)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEventFromEditable(e)) return; // Don't steal keys from inputs/editables
      if (e.key === 'Enter') {
        e.preventDefault();
        maybeRun(false);
      } else if (e.key === ' ') {
        e.preventDefault();
        maybeRun(true);
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () =>
      window.removeEventListener(
        'keydown',
        onKeyDown as EventListenerOrEventListenerObject,
        true
      );
  }, [maybeRun]);

  return (
    <>
      <MenuList
        zIndex={99}
        maxWidth={'300px'}
        maxHeight={'50vh'}
        overflow="hidden"
        paddingX={2}
      >
        <Box
          display="grid"
          gridTemplateColumns="200%" // Double width to accommodate two menus
          transition="transform 0.2s ease-in-out"
          transform={
            currentMenu === 'algorithms' ? 'translateX(0)' : 'translateX(-100%)'
          }
        >
          <Box display="grid" gridTemplateColumns="50% 50%">
            {/* Main menu with New Group and Auto Shapes */}
            <Box
              height={currentMenu === 'algorithms' ? 'auto' : 0}
              overflow="hidden"
              background="inherit"
            >
              {/* New Group option */}
              {onCreateGroup && (
                <>
                  <MenuItem
                    onClick={() => {
                      onCreateGroup();
                      onClose();
                    }}
                  >
                    <HStack width="100%">
                      <SparkelIcon
                        icon={faFolderPlus}
                        size="lg"
                        color={'gray.500'}
                        style={{
                          marginLeft: '12px',
                          marginRight: '12px',
                        }}
                      />
                      <VStack align="start" spacing={0}>
                        <Heading size="xs">New Group</Heading>
                        <Text fontSize="xs" color="gray.500">
                          Create an empty group
                        </Text>
                      </VStack>
                    </HStack>
                  </MenuItem>
                </>
              )}

              {/* Auto Shapes section - only shown when enabled */}
              {enableAutoShapes && (
                <>
                  {onCreateGroup && (
                    <>
                      <Divider my={2} />
                      <HStack mb={1} padding={2} spacing={2}>
                        <Heading size="xs" color="gray.500">
                          Auto Shapes
                        </Heading>
                      </HStack>
                    </>
                  )}

                  {algorithms.map((algorithm, index) => {
                    const algorithmMetadata = getAlgorithmMetadata(algorithm);
                    return (
                      <Fragment key={algorithm}>
                        <MenuItem
                          key={algorithm}
                          data-algo-key={String(algorithm)}
                          closeOnSelect={false}
                          onClick={() => {
                            setCurrentAlgorithmState({
                              type: algorithm,
                              options: getDefaultAlgorithmOptions(
                                algorithm,
                                modelId,
                                globalOffset,
                                [],
                                sheetId,
                                pageNumber,
                                templateRect,
                                sensitivity
                              ),
                            });
                            setCurrentMenu('settings');
                          }}
                        >
                          <HStack width="100%" justify="space-between">
                            <HStack>
                              <SparkelIcon
                                icon={algorithmMetadata.icon}
                                size="lg"
                                color={'gray.500'}
                                style={{
                                  marginLeft: '12px',
                                  marginRight: '12px',
                                }}
                              />
                              <VStack align="start" spacing={0}>
                                <Heading size="xs">
                                  {algorithmMetadata.name}
                                </Heading>
                                <Text fontSize="xs" color="gray.500">
                                  {algorithmMetadata.description}
                                </Text>
                              </VStack>
                            </HStack>
                            <SparkelIcon
                              icon={faChevronRight}
                              color={'gray.500'}
                            />
                          </HStack>
                        </MenuItem>
                        {index !== algorithms.length - 1 && <Divider />}
                      </Fragment>
                    );
                  })}
                </>
              )}
            </Box>

            {/* Settings menu */}
            {currentAlgorithmState?.type && (
              <Box
                height={currentMenu === 'settings' ? 'auto' : 0}
                overflow="hidden"
                background="inherit"
              >
                <HStack mb={1} px={2} spacing={2}>
                  <IconButton
                    aria-label="Back"
                    colorScheme="gray"
                    icon={<SparkelIcon icon={faArrowLeft} color={'gray.500'} />}
                    variant="ghost"
                    size="xs"
                    onClick={() => setCurrentMenu('algorithms')}
                  />
                  <Heading size="xs">
                    {getAlgorithmMetadata(currentAlgorithmState.type).name}
                  </Heading>
                </HStack>
                {/* eslint-disable-next-line complexity */}
                {(() => {
                  const metadata = getAlgorithmMetadata(
                    currentAlgorithmState.type
                  );
                  const creditCost = metadata.creditCost;
                  const hasEnoughCredits =
                    creditsInfo.unlimited ||
                    creditCost === 0 ||
                    creditsInfo.available >= creditCost;

                  const handleRunClick = () => {
                    if (!hasEnoughCredits) {
                      openCreditsModal();
                      return;
                    }
                    const baseName = findNextAvailableAutoShapeName(
                      metadata.name
                    );
                    const name = activeFolderPath
                      ? `${activeFolderPath}/${baseName}`
                      : baseName;
                    createAutoShape(
                      name,
                      currentAlgorithmState.type,
                      currentAlgorithmState.options as AutoShapeAlgorithmOptionsMap[typeof currentAlgorithmState.type]
                    );
                    handleClose();
                  };

                  const creditCostLabel =
                    creditCost > 0
                      ? t('credit-cost.label', {
                          count: creditCost,
                          unit: metadata.creditCostUnitKey
                            ? t(`${metadata.creditCostUnitKey}`)
                            : '',
                        })
                      : null;

                  // Get available credits (project-based if available, tenant-based otherwise)
                  const availableCredits = creditsInfo.isProjectScoped
                    ? creditsInfo.available
                    : tenantCredits?.availableCredits ?? 0;
                  const creditsTooltipLabel = tHeader(
                    'credits.available-count',
                    {
                      count: availableCredits,
                    }
                  );

                  return (
                    <VStack paddingX={2}>
                      {renderSettings}
                      <Divider />
                      <HStack
                        width="100%"
                        justifyContent="flex-end"
                        spacing={2}
                      >
                        {creditCostLabel && (
                          <Tooltip
                            label={creditsTooltipLabel}
                            hasArrow
                            openDelay={400}
                          >
                            <Text
                              fontSize="xs"
                              color={
                                hasEnoughCredits ? 'purple.500' : 'orange.500'
                              }
                              fontStyle="italic"
                            >
                              {creditCostLabel}
                            </Text>
                          </Tooltip>
                        )}
                        <Button
                          isDisabled={hasEnoughCredits && !isSettingsValid}
                          size={'xs'}
                          borderRadius={'md'}
                          colorScheme={hasEnoughCredits ? 'purple' : 'orange'}
                          variant={'outline'}
                          leftIcon={
                            <SparkelIcon
                              icon={
                                hasEnoughCredits ? faPlayCircle : faPlusCircle
                              }
                              color={'inherit'}
                            />
                          }
                          onClick={handleRunClick}
                        >
                          {hasEnoughCredits ? t('run') : t('add-credits')}
                        </Button>
                      </HStack>
                    </VStack>
                  );
                })()}
              </Box>
            )}
          </Box>
        </Box>
      </MenuList>
      {currentAlgorithmState && (
        <InsufficientCreditsModal
          isOpen={isCreditsModalOpen}
          onClose={closeCreditsModal}
          creditsNeeded={
            getAlgorithmMetadata(currentAlgorithmState.type).creditCost
          }
          creditsAvailable={creditsInfo.available}
          isProjectScoped={creditsInfo.isProjectScoped}
          tenantCreditsAvailable={tenantCredits?.availableCredits ?? 0}
          isAdmin={isAdmin}
          renewalDate={creditsInfo.renewalDate}
          renewalAmount={creditsInfo.renewalAmount}
        />
      )}
    </>
  );
};
